// Gestion des Produits - Espace Pro
// Connecté aux vraies APIs

import { useState, useEffect } from 'react';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Package, Plus, Search, Filter, Edit, Trash2, Eye, MoreVertical,
  ArrowLeft, AlertCircle, CheckCircle2, Pause, Play, Copy,
  TrendingUp, Image as ImageIcon, X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { sellerProductsAPI } from '../../services/sellerAPI';

// Statuts produits
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Edit },
  pending: { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  active: { label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  paused: { label: 'En pause', color: 'bg-orange-100 text-orange-700', icon: Pause },
  out_of_stock: { label: 'Rupture', color: 'bg-red-100 text-red-700', icon: AlertCircle },
  archived: { label: 'Archivé', color: 'bg-gray-100 text-gray-500', icon: Trash2 },
};

// Carte produit
function ProductCard({ product, onEdit, onDelete, onToggleStatus, onDuplicate }) {
  const [showMenu, setShowMenu] = useState(false);
  const status = STATUS_CONFIG[product.status] || STATUS_CONFIG.draft;
  const StatusIcon = status.icon;
  
  const mainImage = product.images?.[0]?.url || product.images?.[0] || null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all group"
    >
      {/* Image */}
      <div className="relative aspect-square bg-gray-100">
        {mainImage ? (
          <img src={mainImage} alt={product.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ImageIcon size={40} className="text-gray-300" />
          </div>
        )}
        
        {/* Badge statut */}
        <span className={`absolute top-2 left-2 px-2 py-1 rounded-lg text-xs font-medium ${status.color}`}>
          {status.label}
        </span>
        
        {/* Stock badge */}
        {product.stock !== undefined && (
          <span className={`absolute top-2 right-2 px-2 py-1 rounded-lg text-xs font-medium ${
            product.stock <= 0 ? 'bg-red-500 text-white' :
            product.stock <= 5 ? 'bg-yellow-500 text-white' :
            'bg-gray-800 text-white'
          }`}>
            {product.stock <= 0 ? 'Rupture' : `Stock: ${product.stock}`}
          </span>
        )}
        
        {/* Actions overlay */}
        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onEdit(product)}
            className="p-2 bg-white rounded-lg hover:bg-gray-100"
          >
            <Edit size={18} className="text-gray-700" />
          </button>
          <button
            onClick={() => window.open(`/produits/${product.id}`, '_blank')}
            className="p-2 bg-white rounded-lg hover:bg-gray-100"
          >
            <Eye size={18} className="text-gray-700" />
          </button>
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-2 bg-white rounded-lg hover:bg-gray-100"
            >
              <MoreVertical size={18} className="text-gray-700" />
            </button>
            
            {/* Menu dropdown */}
            <AnimatePresence>
              {showMenu && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-xl border border-gray-200 py-1 min-w-[160px] z-10"
                >
                  <button
                    onClick={() => { onDuplicate(product); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Copy size={14} /> Dupliquer
                  </button>
                  {product.status === 'active' ? (
                    <button
                      onClick={() => { onToggleStatus(product, 'paused'); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50"
                    >
                      <Pause size={14} /> Mettre en pause
                    </button>
                  ) : (
                    <button
                      onClick={() => { onToggleStatus(product, 'active'); setShowMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-green-600 hover:bg-green-50"
                    >
                      <Play size={14} /> Activer
                    </button>
                  )}
                  <button
                    onClick={() => { onDelete(product); setShowMenu(false); }}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="p-4">
        <h3 className="font-medium text-gray-900 line-clamp-2 mb-2 min-h-[48px]">
          {product.title}
        </h3>
        
        <p className="text-sm text-gray-500 mb-2 line-clamp-1">
          {product.category}{product.subcategory && ` › ${product.subcategory}`}
        </p>

        <div className="flex items-center justify-between">
          <div>
            <span className="text-xl font-bold text-gray-900">{product.price?.toFixed(2)} €</span>
            {product.compare_at_price && product.compare_at_price > product.price && (
              <span className="text-sm text-gray-400 line-through ml-2">
                {product.compare_at_price.toFixed(2)} €
              </span>
            )}
          </div>
          
          {/* Stats */}
          <div className="flex items-center gap-2 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Eye size={12} /> {product.view_count || 0}
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp size={12} /> {product.sales_count || 0}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Formulaire produit (modal)
function ProductForm({ product, onSave, onClose }) {
  const [formData, setFormData] = useState({
    title: product?.title || '',
    description: product?.description || '',
    short_description: product?.short_description || '',
    category: product?.category || 'Outillage',
    subcategory: product?.subcategory || '',
    price: product?.price || '',
    compare_at_price: product?.compare_at_price || '',
    stock: product?.stock ?? 0,
    sku: product?.sku || '',
    tags: product?.tags?.join(', ') || '',
    status: product?.status || 'draft',
  });
  const [saving, setSaving] = useState(false);

  const categories = [
    'Outillage', 'Plomberie', 'Électricité', 'Peinture', 'Menuiserie',
    'Maçonnerie', 'Carrelage', 'Chauffage', 'Jardin', 'Sécurité', 'Autre'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price) {
      toast.error('Titre et prix requis');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
        compare_at_price: formData.compare_at_price ? parseFloat(formData.compare_at_price) : null,
        stock: parseInt(formData.stock) || 0,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
      };

      if (product?.id) {
        await sellerProductsAPI.update(product.id, data);
        toast.success('Produit mis à jour');
      } else {
        await sellerProductsAPI.create(data);
        toast.success('Produit créé');
      }
      onSave();
    } catch (e) {
      toast.error(e.message || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-900">
            {product ? 'Modifier le produit' : 'Nouveau produit'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Titre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              placeholder="Nom du produit"
              required
            />
          </div>

          {/* Description courte */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description courte</label>
            <Input
              value={formData.short_description}
              onChange={(e) => setFormData({...formData, short_description: e.target.value})}
              placeholder="Résumé en une ligne"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              placeholder="Description détaillée..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>

          {/* Catégorie */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sous-catégorie</label>
              <Input
                value={formData.subcategory}
                onChange={(e) => setFormData({...formData, subcategory: e.target.value})}
                placeholder="Ex: Perceuses"
              />
            </div>
          </div>

          {/* Prix */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix (€) *</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => setFormData({...formData, price: e.target.value})}
                placeholder="0.00"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prix barré (€)</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.compare_at_price}
                onChange={(e) => setFormData({...formData, compare_at_price: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Stock</label>
              <Input
                type="number"
                min="0"
                value={formData.stock}
                onChange={(e) => setFormData({...formData, stock: e.target.value})}
                placeholder="0"
              />
            </div>
          </div>

          {/* SKU et Tags */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">SKU</label>
              <Input
                value={formData.sku}
                onChange={(e) => setFormData({...formData, sku: e.target.value})}
                placeholder="REF-001"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData({...formData, tags: e.target.value})}
                placeholder="tag1, tag2, tag3"
              />
            </div>
          </div>

          {/* Statut */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({...formData, status: e.target.value})}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-white"
            >
              <option value="draft">Brouillon</option>
              <option value="active">Actif (en vente)</option>
              <option value="paused">En pause</option>
            </select>
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 bg-gray-50">
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={saving}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {saving ? 'Enregistrement...' : product ? 'Enregistrer' : 'Créer'}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function ProduitsPage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingProduct, setEditingProduct] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const data = await sellerProductsAPI.getAll(statusFilter || null);
      setProducts(data);
    } catch (e) {
      console.error('Error fetching products:', e);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [statusFilter]);

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowForm(true);
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Supprimer "${product.title}" ?`)) return;
    
    try {
      await sellerProductsAPI.delete(product.id);
      toast.success('Produit archivé');
      fetchProducts();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggleStatus = async (product, newStatus) => {
    try {
      await sellerProductsAPI.toggleStatus(product.id, newStatus);
      toast.success(`Produit ${newStatus === 'active' ? 'activé' : 'mis en pause'}`);
      fetchProducts();
    } catch (e) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDuplicate = async (product) => {
    try {
      const newProduct = {
        title: `${product.title} (copie)`,
        description: product.description,
        short_description: product.short_description,
        category: product.category,
        subcategory: product.subcategory,
        price: product.price,
        compare_at_price: product.compare_at_price,
        stock: 0,
        tags: product.tags,
        status: 'draft'
      };
      await sellerProductsAPI.create(newProduct);
      toast.success('Produit dupliqué');
      fetchProducts();
    } catch (e) {
      toast.error('Erreur lors de la duplication');
    }
  };

  const handleSave = () => {
    setShowForm(false);
    setEditingProduct(null);
    fetchProducts();
  };

  const filteredProducts = products.filter(p =>
    searchQuery === '' || 
    p.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats rapides
  const stats = {
    total: products.length,
    active: products.filter(p => p.status === 'active').length,
    lowStock: products.filter(p => p.stock > 0 && p.stock <= 5).length,
    outOfStock: products.filter(p => p.stock <= 0).length,
  };

  return (
    <div className="max-w-7xl mx-auto" data-testid="produits-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mes produits</h1>
          <p className="text-gray-500">{stats.total} produit(s) • {stats.active} actif(s)</p>
        </div>
        <Button 
          onClick={() => { setEditingProduct(null); setShowForm(true); }}
          className="bg-orange-500 hover:bg-orange-600 rounded-xl"
        >
          <Plus size={18} className="mr-2" />
          Nouveau produit
        </Button>
      </div>

      {/* Stats rapides */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </div>
        <div className="bg-green-50 rounded-xl p-4 border border-green-200">
          <p className="text-2xl font-bold text-green-700">{stats.active}</p>
          <p className="text-sm text-green-600">Actifs</p>
        </div>
        <div className="bg-yellow-50 rounded-xl p-4 border border-yellow-200">
          <p className="text-2xl font-bold text-yellow-700">{stats.lowStock}</p>
          <p className="text-sm text-yellow-600">Stock bas</p>
        </div>
        <div className="bg-red-50 rounded-xl p-4 border border-red-200">
          <p className="text-2xl font-bold text-red-700">{stats.outOfStock}</p>
          <p className="text-sm text-red-600">Rupture</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Rechercher un produit..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 rounded-xl"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl bg-white text-sm"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="draft">Brouillons</option>
            <option value="paused">En pause</option>
            <option value="out_of_stock">Rupture de stock</option>
          </select>
        </div>
      </div>

      {/* Liste des produits */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-4 border-orange-500/30 border-t-orange-500 rounded-full animate-spin" />
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
          <Package size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-bold text-gray-900 mb-2">Aucun produit</h3>
          <p className="text-gray-500 mb-6">
            {searchQuery || statusFilter 
              ? "Aucun produit ne correspond à tes critères"
              : "Commence par ajouter ton premier produit"}
          </p>
          <Button 
            onClick={() => { setEditingProduct(null); setShowForm(true); }}
            className="bg-orange-500 hover:bg-orange-600 rounded-xl"
          >
            <Plus size={18} className="mr-2" />
            Ajouter un produit
          </Button>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
              onDuplicate={handleDuplicate}
            />
          ))}
        </div>
      )}

      {/* Modal formulaire */}
      <AnimatePresence>
        {showForm && (
          <ProductForm
            product={editingProduct}
            onSave={handleSave}
            onClose={() => { setShowForm(false); setEditingProduct(null); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
